import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { Button, HSpacing, Loader, Tag } from '@hh.ru/magritte-ui';
import { GearOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import BrokerClient from '@hh.ru/react-filter-components-plugin/lib/client/brokerClient';
import { ReportSection } from './ReportSection';
import { Settings } from './Settings';
import { useDevLoaderSettings } from './Settings/hooks/useDevLoaderSettings';
import styles from './styles.less';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
let _DevBuildLoader = () => null;
const NON_BREAKING_SPACE = '\u00a0';
const MAX_PERCENTAGE = 100;
const WRAPPER_ID = 'dev-loader-wrapper';
let reloadTimeoutId = null;
if (process.env.HH_BUILD_MODE === 'development') {
  const DevBuildLoaderPrivate = ({
    serviceName
  }) => {
    const [percent, setPercent] = useState(MAX_PERCENTAGE);
    const previousPercent = useRef(percent);
    const [{
      liveReload,
      reloadDelay,
      alwaysShow
    }, setSettings] = useDevLoaderSettings(serviceName);
    const [isReady, setIsReady] = useState(false);
    const [isSettingsShown, setIsSettingsShown] = useState(false);
    const [isReportShown, setIsReportShown] = useState(false);
    const [waitForReload, setWaitForReload] = useState(false);
    const [compilationError, setCompilationError] = useState(null);
    const [webSocketClosedError, setWebSocketClosedError] = useState(null);
    const [userLogLink, setUserLogLink] = useState('');
    const [didWSConnect, setDidWSConnect] = useState(false);
    useEffect(() => {
      if (!liveReload || previousPercent.current === percent) {
        return;
      }
      if (percent === MAX_PERCENTAGE) {
        setWaitForReload(true);
        /* Статика заливается какое-то время в под с сервисом*/
        reloadTimeoutId = setTimeout(() => {
          window.location.reload();
        }, reloadDelay);
      } else if (reloadTimeoutId) {
        // этот блок нужен для случая, когда в процессе сборки поменялся еще раз файл, чтобы не перезагружать
        // страницу по несколько раз, а сбросить текущее состояние
        clearTimeout(reloadTimeoutId);
        setWaitForReload(false);
      }
      previousPercent.current = percent;
    }, [percent, liveReload, reloadDelay]);
    useEffect(() => {
      if (!BrokerClient) {
        return undefined;
      }
      if (BrokerClient.readyState === WebSocket.OPEN) {
        setDidWSConnect(true);
      }
      const onWSConnectOpen = () => {
        setDidWSConnect(true);
        setWebSocketClosedError(null);
      };
      BrokerClient.addEventListener('open', onWSConnectOpen);
      const onMessage = message => {
        try {
          const data = JSON.parse(message.data);
          switch (data?.action) {
            case 'build-progress-update':
              setPercent(data.details.progress);
              break;
            case 'build-error':
              setCompilationError(data.details.message);
              break;
            case 'build-error-clear':
              setCompilationError(null);
              break;
            case 'build-log-link-message':
              setUserLogLink(data.details.userLogLink);
              break;
          }
        } catch (_) {
          console.error('Decode error');
        }
      };
      BrokerClient.addEventListener('message', onMessage);
      const onClose = event => {
        const retryAttempts = BrokerClient?.attempts;
        const shouldShowDevLoader = retryAttempts !== undefined && (retryAttempts > 1 || BrokerClient?.destroyed);
        if (shouldShowDevLoader) {
          setWebSocketClosedError(`WebSocket closed (code: ${event.code})`);
        }
      };

      // @ts-expect-error
      BrokerClient.addEventListener('close', onClose);
      return () => {
        BrokerClient?.removeEventListener('message', onMessage);
        BrokerClient?.removeEventListener('open', onWSConnectOpen);
        // @ts-expect-error
        BrokerClient?.removeEventListener('close', onClose);
      };
    }, []);
    useEffect(() => {
      const devLoaderWrapper = document.querySelector(`#${WRAPPER_ID}`);
      if (devLoaderWrapper === null) {
        const wrapper = document.createElement('div');
        wrapper.classList.add(styles.containerForDevLoaders);
        wrapper.id = WRAPPER_ID;
        document.body.prepend(wrapper);
      }
      const globalVars = serviceName === 'xhh' ? window.globalVars : window.globalServiceVars[serviceName];
      if (!globalVars.devLoaderInited) {
        setIsReady(true);
        globalVars.devLoaderInited = true;
      }
    }, [serviceName]);
    useEffect(() => {
      const handleKeydown = event => {
        if (event.ctrlKey && event.shiftKey && (event.key === '@' || event.key === '2')) {
          event.preventDefault();
          setSettings({
            alwaysShow: !alwaysShow
          });
        }
      };
      document.addEventListener('keydown', handleKeydown);
      return () => {
        document.removeEventListener('keydown', handleKeydown);
      };
    }, [setSettings, alwaysShow]);
    const buildInProgress = percent >= 0 && (percent < MAX_PERCENTAGE || waitForReload);
    const showWSInfo = didWSConnect && !!webSocketClosedError;
    const shouldShow = buildInProgress || showWSInfo || isReportShown || isSettingsShown || !!compilationError;
    if (!alwaysShow && !shouldShow) {
      return null;
    }
    const renderBuildStep = () => {
      if (waitForReload) {
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: ["waiting ", reloadDelay, " ms", /*#__PURE__*/_jsx(HSpacing, {
            default: 4
          }), /*#__PURE__*/_jsx(Loader, {
            size: 16,
            initial: "positive"
          })]
        });
      }
      if (webSocketClosedError) {
        return /*#__PURE__*/_jsx(Tag, {
          style: "negative",
          children: webSocketClosedError
        });
      }
      if (compilationError) {
        return /*#__PURE__*/_jsx(Tag, {
          style: "negative",
          children: "compilation error"
        });
      }
      if (buildInProgress) {
        return /*#__PURE__*/_jsx("progress", {
          className: styles.progress,
          max: MAX_PERCENTAGE,
          value: percent,
          children: percent
        });
      }
      return 'idle';
    };
    return /*#__PURE__*/_jsx(_Fragment, {
      children: isReady && /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs("div", {
          className: classnames(styles.wrapper, {
            [styles.wrapperError]: !!compilationError || !!webSocketClosedError
          }),
          children: [/*#__PURE__*/_jsxs("label", {
            className: styles.progressLabel,
            children: [serviceName, ":", NON_BREAKING_SPACE, renderBuildStep()]
          }), /*#__PURE__*/_jsx(Button, {
            onClick: () => {
              setIsReportShown(isReportShown => !isReportShown);
              setIsSettingsShown(false);
            },
            type: "button",
            size: "small",
            mode: isReportShown ? 'secondary' : 'primary',
            style: "accent",
            children: "\u041D\u0443\u0436\u043D\u0430 \u043F\u043E\u043C\u043E\u0449\u044C!"
          }), /*#__PURE__*/_jsx(Button, {
            mode: isSettingsShown ? 'secondary' : 'tertiary',
            icon: /*#__PURE__*/_jsx(GearOutlinedSize24, {
              initialColor: "primary"
            }),
            type: "button",
            size: "small",
            style: "neutral",
            onClick: () => {
              setIsSettingsShown(isSettingsShown => !isSettingsShown);
              setIsReportShown(false);
            }
          }), (isReportShown || isSettingsShown) && /*#__PURE__*/_jsx("div", {
            className: styles.contentWrapper,
            children: /*#__PURE__*/_jsxs("div", {
              className: styles.contentInner,
              children: [isReportShown && /*#__PURE__*/_jsx(ReportSection, {
                userLogLink: userLogLink,
                compilationError: compilationError,
                webSocketClosedError: webSocketClosedError
              }), isSettingsShown && /*#__PURE__*/_jsx(Settings, {
                serviceName: serviceName
              })]
            })
          })]
        }), !!compilationError && /*#__PURE__*/_jsx("div", {
          className: styles.errorWrapper,
          children: /*#__PURE__*/_jsx("pre", {
            children: compilationError
          })
        })]
      }), document.querySelector(`#${WRAPPER_ID}`))
    });
  };
  _DevBuildLoader = DevBuildLoaderPrivate;
}
const DevBuildLoader = _DevBuildLoader;
export default DevBuildLoader;
//# sourceMappingURL=index.js.map